/**
 * Painel do setor e chat com o agente do setor — Nexora Grid (S3 v2).
 *
 * Criado em: 16/10/2026 · Última modificação: 25/10/2026 · Versão: 1.2.1
 *
 * Clicar num setor na legenda destaca o setor na cena e abre este painel:
 * - reservatórios, pressões, macromedidores, bombas e ocorrências na hora da simulação;
 * - chat com o agente do setor (ADR-0011).
 * v1.1: o chat fala com o AGENTE REAL (Claude, pelo serviço de agentes — `POST /setor/perguntar`, ADR-0011),
 * enviando a situação do setor naquele instante. Se o serviço não estiver disponível, responde localmente pelos
 * números do setor e diz o motivo.
 * v1.2 (17/10/2026): chat comum (`ChatAssistente`) no TOPO do painel, no mesmo padrão do assistente do CCO: perguntas
 * padrão do setor respondidas direto da telemetria (sem LLM); análise pelo agente do setor com esta situação.
 */
import { ChatAssistente } from "../ChatAssistente";
import { NBR_MAX, NBR_MIN } from "./simulacao";
import type { SituacaoSetor } from "./setor";

const FORMAS = { 1: "◆", 2: "▲", 3: "●", 4: "■" } as const;
const CLASSES = { 1: "p1", 2: "p2", 3: "p3", 4: "p4" } as const;
const fmt = (n: number, casas = 1) => (Number.isFinite(n) ? n.toLocaleString("pt-BR", { maximumFractionDigits: casas,
  minimumFractionDigits: casas }) : "—");
const horaTexto = (h: number) => `${String(Math.floor(h) % 24).padStart(2, "0")}:${String(Math.floor((h % 1) * 60)).padStart(2, "0")}`;

/** Situação do setor no formato enviado ao agente (números arredondados, com unidade no nome). */
export function situacaoParaAgente(s: SituacaoSetor) {
  const um = (n: number) => Math.round(n * 10) / 10;
  return {
    origem: s.origem === "ao vivo" ? "telemetria ao vivo (dados reais dos sensores da operadora)"
      : "simulação (rede importada do DWG, não calibrada; sem telemetria real)",
    hora: horaTexto(s.hora), trechos: s.trechos, ligacoes: s.casas, registros_fechados: s.registrosFechados,
    reservatorios: s.reservatorios.map((r) => ({ id: r.id, volume_m3: r.volume, nivel_pct: Math.round(r.nivel) })),
    pressao: s.pressao ? { min_mca: um(s.pressao.min), media_mca: um(s.pressao.media), max_mca: um(s.pressao.max),
      dentro_nbr_pct: Math.round(s.pressao.dentroPct), pontos: s.pressao.nos } : null,
    macromedidores: s.macromedidores.map((m) => ({ id: m.id, vazao_m3h: um(m.vazaoLps * 3.6) })),
    bombas: s.bombas.map((b) => ({ id: b.id, tipo: b.tipo, ligada: b.ligada })),
    ocorrencias: s.ocorrencias.map((o) => ({ prioridade: o.prioridade, texto: o.texto })),
  };
}

export function PainelSetor({ situacao, empresa, aoFechar }: {
  situacao: SituacaoSetor; empresa: string | null; aoFechar: () => void;
}) {
  const s = situacao;
  return (
    <aside className="painel setor" aria-label={`Setor ${s.setor}`}>
      <header className="setor-topo">
        <strong>SETOR {s.setor}</strong>
        <span className={s.origem === "ao vivo" ? "selo-ia" : "suave"}>{horaTexto(s.hora)} · {s.origem}</span>
        <button type="button" onClick={aoFechar} aria-label="Fechar painel do setor">✕</button>
      </header>
      <ChatAssistente empresa={empresa} setor={s.setor} situacao={() => situacaoParaAgente(s)} titulo="Agente do setor"
        rotuloEntrada="Mensagem ao agente do setor" placeholder="Pergunte ao agente do setor…"
        saudacao={`Sou o agente do setor ${s.setor}. Perguntas padrão (nível, pressão, bombas, alarmes) respondo direto da telemetria; análise vai ao agente de IA.`}
        sugestoes={["qual a pressão do setor?", "tem alarme no setor?", "qual o problema mais grave do setor agora?"]} />
      <p className="suave">{s.trechos} trechos · {s.casas.toLocaleString("pt-BR")} ligações · {s.registrosFechados} registros fechados</p>
      <section>
        <h3>Reservatórios</h3>
        {s.reservatorios.length ? s.reservatorios.map((r) => (
          <div key={r.id} className="linha-dado">
            <span>{r.id}</span>
            <span className="barra-nivel"><span style={{ width: `${Math.max(0, Math.min(100, r.nivel))}%` }} /></span>
            <b>{fmt(r.nivel, 0)}%</b>
          </div>
        )) : <p className="suave">nenhum no setor</p>}
      </section>
      <section>
        <h3>Pressão da rede</h3>
        {s.pressao ? (
          <p className="valores">mín <b>{fmt(s.pressao.min)}</b> · méd <b>{fmt(s.pressao.media)}</b> · máx <b>{fmt(s.pressao.max)}</b> mca
            <br /><span className="suave">{fmt(s.pressao.dentroPct, 0)}% dos {s.pressao.nos} pontos entre {NBR_MIN} e {NBR_MAX} mca</span></p>
        ) : <p className="suave">sem nós simulados</p>}
      </section>
      <section>
        <h3>Macromedidores</h3>
        {s.macromedidores.length ? s.macromedidores.map((m) => (
          <div key={m.id} className="linha-dado"><span>{m.id}</span><b>{fmt(m.vazaoLps * 3.6)} m³/h</b></div>
        )) : <p className="suave">nenhum no setor</p>}
      </section>
      <section>
        <h3>Bombas</h3>
        {s.bombas.length ? s.bombas.map((b) => (
          <div key={b.id} className="linha-dado"><span>{b.id}</span>
            <b className={b.ligada ? "ligada" : "desligada"}>{b.ligada ? "ligada" : "desligada"}</b></div>
        )) : <p className="suave">nenhuma no setor</p>}
      </section>
      <section>
        <h3>Ocorrências e falhas</h3>
        <ul className="ocorrencias">
          {s.ocorrencias.map((o, k) => <li key={k} className={CLASSES[o.prioridade]}><span>{FORMAS[o.prioridade]}</span>{o.texto}</li>)}
        </ul>
      </section>
    </aside>
  );
}
